import React, { useEffect, useState, useRef } from "react";
import { api, formatCHF, currentMonthString, formatMonat, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { CheckCircle2, Lock, Unlock, Upload, AlertCircle, Plus, Download, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "step1", label: "1. Alle Tagesrapporte hochgeladen?" },
  { key: "step2", label: "2. Alle FT / QM / Aldi / Spedition Einsätze erfasst?" },
  { key: "step3", label: "3. Kundenabrechnung hochgeladen?" },
  { key: "step4", label: "4. Automatischer Abgleich durchgeführt?" },
  { key: "step5", label: "5. Differenzen geprüft und kommentiert?" },
  { key: "step6", label: "6. Fahrerlöhne geprüft?" },
  { key: "step7", label: "7. Verladung/Entladung pro Mitarbeiter freigegeben?" },
  { key: "step8", label: "8. Fahrzeugkosten erfasst?" },
];

const DIFF_TYPES = [
  "fehlt bei uns",
  "fehlt in Kundenabrechnung",
  "Zeitdifferenz",
  "KM-Differenz",
  "falsche Tournummer",
  "falscher Fahrer",
  "anderer Betrag",
  "doppelte Erfassung",
];

export default function Monatsabschluss() {
  const [monat, setMonat] = useState(currentMonthString());
  const [closing, setClosing] = useState({ status: "offen", schritte: {}, notizen: "" });
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffForm, setDiffForm] = useState({ typ: "fehlt bei uns", datum: "", tournummer: "", fahrer: "", betrag: 0, kommentar: "" });
  const [uploading, setUploading] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    const [c, i, d, inv] = await Promise.all([
      api.get(`/closings/${monat}`),
      api.get(`/reconciliation/${monat}`),
      api.get(`/dashboard`, { params: { monat } }),
      api.get(`/reconciliation/${monat}/last-invoice`).catch(() => ({ data: null })),
    ]);
    setClosing({ status: "offen", schritte: {}, notizen: "", ...c.data });
    setItems(i.data);
    setDashboard(d.data);
    setLastInvoice(inv.data);
  };
  useEffect(() => { load(); }, [monat]);

  const updateStep = async (key, value) => {
    const next = { ...closing, schritte: { ...(closing.schritte || {}), [key]: value } };
    setClosing(next);
    await api.put(`/closings/${monat}`, next);
  };

  const updateNotizen = async (text) => {
    setClosing({ ...closing, notizen: text });
  };

  const saveNotes = async () => {
    await api.put(`/closings/${monat}`, closing);
    toast.success("Notizen gespeichert");
  };

  const lock = async () => {
    if (!window.confirm("Monat wirklich abschliessen und sperren?")) return;
    await api.post(`/closings/${monat}/lock`);
    toast.success("Monat abgeschlossen");
    load();
  };

  const unlock = async () => {
    if (!window.confirm("Monat wieder entsperren?")) return;
    await api.post(`/closings/${monat}/unlock`);
    toast.success("Monat entsperrt");
    load();
  };

  const uploadInvoice = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("monat", monat);
    try {
      await api.post(`/reconciliation/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Kundenabrechnung hochgeladen");
      await updateStep("step3", true);
      load();
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runAutoMatch = async () => {
    if (!lastInvoice) {
      toast.error("Bitte zuerst eine Kundenabrechnung hochladen.");
      return;
    }
    if (!window.confirm("Auto-Abgleich starten? Bestehende automatisch erstellte Differenzen werden ersetzt (manuelle bleiben erhalten).")) return;
    setAutoMatching(true);
    try {
      const start = await api.post(`/reconciliation/${monat}/auto-match`);
      const jobId = start.data.job_id;
      toast.info("Auto-Abgleich gestartet — dies kann bis zu 90 Sekunden dauern…");

      // Poll until done/error (max ~3 min)
      const startTs = Date.now();
      let finished = false;
      while (!finished && Date.now() - startTs < 180000) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const st = await api.get(`/reconciliation/auto-match/status/${jobId}`);
          const status = st.data.status;
          if (status === "done") {
            toast.success(`${st.data.invoice_lines} Positionen verarbeitet · ${st.data.diffs_inserted} Differenzen erkannt`);
            await updateStep("step4", true);
            await load();
            finished = true;
          } else if (status === "error") {
            toast.error("Auto-Abgleich fehlgeschlagen: " + (st.data.error || "Unbekannt"));
            finished = true;
          }
        } catch (e) {
          // continue polling
        }
      }
      if (!finished) {
        toast.error("Auto-Abgleich Zeitüberschreitung — bitte erneut versuchen oder Differenzen manuell prüfen.");
      }
    } catch (e) {
      toast.error("Auto-Abgleich konnte nicht gestartet werden: " + (e.response?.data?.detail || e.message));
    } finally {
      setAutoMatching(false);
    }
  };

  const addDiff = async () => {
    try {
      await api.post(`/reconciliation/${monat}/manual`, {
        ...diffForm,
        betrag: Number(diffForm.betrag) || 0,
        status: "offen",
      });
      toast.success("Differenz erfasst");
      setDiffOpen(false);
      setDiffForm({ typ: "fehlt bei uns", datum: "", tournummer: "", fahrer: "", betrag: 0, kommentar: "" });
      load();
    } catch { toast.error("Fehler"); }
  };

  const updateDiff = async (id, patch) => {
    await api.put(`/reconciliation/items/${id}`, patch);
    load();
  };

  const removeDiff = async (id) => {
    if (!window.confirm("Differenz löschen?")) return;
    await api.delete(`/reconciliation/items/${id}`);
    load();
  };

  const downloadExcel = async () => {
    const r = await api.get(`/export/excel/${monat}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `NIVARA_${monat}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const r = await api.get(`/export/pdf/${monat}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `NIVARA_${monat}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const isLocked = closing.status === "gesperrt";

  return (
    <PageContainer>
      <PageHeader title="Monatsabschluss" subtitle={`Abschluss-Workflow für ${formatMonat(monat)}`} testId="closing-page">
        <MonthPicker value={monat} onChange={setMonat} testId="cl-month-picker" />
        {isLocked ? (
          <Button onClick={unlock} variant="outline" data-testid="unlock-button" className="border-neutral-300">
            <Unlock className="h-4 w-4 mr-2" /> Entsperren
          </Button>
        ) : (
          <Button onClick={lock} className="bg-[#008A00] hover:bg-[#006A00] text-white" data-testid="lock-button">
            <Lock className="h-4 w-4 mr-2" /> Monat abschliessen
          </Button>
        )}
      </PageHeader>

      <div className="mb-4">
        <Badge className={isLocked ? "bg-[#008A00] text-white" : "bg-[#FFB800] text-neutral-900"}>
          Status: {isLocked ? "Abgeschlossen" : "In Bearbeitung"}
        </Badge>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card className="p-4 border-neutral-200 bg-white">
            <div className="text-xs uppercase tracking-wider text-neutral-500">Umsatz</div>
            <div className="text-lg font-semibold nivara-num">{formatCHF(dashboard.gesamt_umsatz)}</div>
          </Card>
          <Card className="p-4 border-neutral-200 bg-white">
            <div className="text-xs uppercase tracking-wider text-neutral-500">Fahrerkosten</div>
            <div className="text-lg font-semibold nivara-num">{formatCHF(dashboard.fahrer_kosten)}</div>
          </Card>
          <Card className="p-4 border-neutral-200 bg-white">
            <div className="text-xs uppercase tracking-wider text-neutral-500">Fahrzeugkosten</div>
            <div className="text-lg font-semibold nivara-num">{formatCHF(dashboard.fahrzeug_kosten)}</div>
          </Card>
          <Card className="p-4 border-neutral-200 bg-white">
            <div className="text-xs uppercase tracking-wider text-neutral-500">Bruttogewinn</div>
            <div className={`text-lg font-semibold nivara-num ${dashboard.brutto_gewinn >= 0 ? "text-[#008A00]" : "text-[#E60000]"}`}>{formatCHF(dashboard.brutto_gewinn)}</div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 border-neutral-200 bg-white" data-testid="steps-card">
          <h3 className="text-base font-semibold mb-4">Abschluss-Schritte</h3>
          <div className="space-y-3">
            {STEPS.map((s) => (
              <label key={s.key} className="flex items-start gap-3 p-2 -mx-2 rounded-md hover:bg-neutral-50 cursor-pointer" data-testid={`step-${s.key}`}>
                <Checkbox
                  checked={!!closing.schritte?.[s.key]}
                  onCheckedChange={(v) => updateStep(s.key, !!v)}
                  disabled={isLocked}
                  className="mt-0.5"
                />
                <span className="text-sm">{s.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-5 space-y-2">
            <Label>Notizen zum Monatsabschluss</Label>
            <Textarea value={closing.notizen || ""} onChange={(e) => updateNotizen(e.target.value)} disabled={isLocked} />
            <Button onClick={saveNotes} variant="outline" size="sm" disabled={isLocked} className="border-neutral-300">Notizen speichern</Button>
          </div>
        </Card>

        <Card className="p-5 border-neutral-200 bg-white" data-testid="invoice-card">
          <h3 className="text-base font-semibold mb-4">Kundenabrechnung & Auto-Abgleich</h3>
          <input type="file" ref={fileRef} className="hidden" accept=".pdf,.xlsx,.xls,.csv,image/*" onChange={(e) => e.target.files?.[0] && uploadInvoice(e.target.files[0])} />
          <div className="space-y-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || isLocked} data-testid="upload-invoice-button" className="w-full border-neutral-300 justify-start">
              <Upload className="h-4 w-4 mr-2" />{uploading ? "Lade hoch…" : "Kundenabrechnung hochladen"}
            </Button>
            {lastInvoice && (
              <div className="text-xs text-neutral-600 px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
                <span className="font-medium">Aktuell:</span> {lastInvoice.filename} · hochgeladen {new Date(lastInvoice.created_at).toLocaleString("de-CH")}
              </div>
            )}
            <Button
              onClick={runAutoMatch}
              disabled={!lastInvoice || autoMatching || isLocked}
              data-testid="auto-match-button"
              className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white justify-start"
            >
              {autoMatching ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Zap className="h-4 w-4 mr-2" />}
              {autoMatching ? "Auto-Abgleich läuft…" : "KI-Auto-Abgleich starten"}
            </Button>
            <div className="text-[11px] text-neutral-500 pl-1">
              Parst die hochgeladene 7Days-Abrechnung mit GPT-5.2 und gleicht sie mit deinen Tagesrapporten, FT-Einsätzen, QM, Spedi und Aldi ab.
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-neutral-100">
            <h4 className="text-sm font-medium mb-2">Schnell-Exporte</h4>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadPdf} className="border-neutral-300" data-testid="cl-export-pdf"><Download className="h-3.5 w-3.5 mr-2" /> PDF</Button>
              <Button variant="outline" size="sm" onClick={downloadExcel} className="border-neutral-300" data-testid="cl-export-excel"><Download className="h-3.5 w-3.5 mr-2" /> Excel</Button>
            </div>
          </div>
        </Card>
      </div>

      <Card className="border-neutral-200 bg-white overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">Differenzen / Reconciliation</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Differenzen aus dem Abgleich Kundenabrechnung vs. Tagesrapporte</p>
          </div>
          <Button onClick={() => setDiffOpen(true)} variant="outline" size="sm" disabled={isLocked} data-testid="add-diff-button" className="border-neutral-300">
            <Plus className="h-4 w-4 mr-2" /> Differenz erfassen
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="diffs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Typ</TableHead><TableHead>Datum</TableHead><TableHead>Tour</TableHead><TableHead>Fahrer</TableHead>
                <TableHead className="text-right">Betrag</TableHead><TableHead>Kommentar</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-8 flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#008A00]" /> Keine offenen Differenzen.</TableCell></TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.id} className={it.status === "offen" ? "bg-red-50/50" : ""}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className="text-xs w-fit">{it.typ}</Badge>
                      {it.source === "auto" && (
                        <Badge className="bg-[#0055FF]/10 text-[#0055FF] text-[10px] w-fit border-0">KI auto</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{formatDatum(it.datum) || "—"}</TableCell>
                  <TableCell className="font-mono">{it.tournummer || "—"}</TableCell>
                  <TableCell>{it.fahrer || "—"}</TableCell>
                  <TableCell className="nivara-num">{it.betrag != null ? formatCHF(it.betrag) : "—"}</TableCell>
                  <TableCell className="text-xs max-w-[260px] truncate" title={it.kommentar || ""}>{it.kommentar || "—"}</TableCell>
                  <TableCell>
                    <Select value={it.status} onValueChange={(v) => updateDiff(it.id, { kommentar: it.kommentar || "", korrektur_betrag: it.korrektur_betrag ?? null, status: v })}>
                      <SelectTrigger className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{["offen","geprüft","akzeptiert","korrigiert"].map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => removeDiff(it.id)}>
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="max-w-xl" data-testid="diff-dialog">
          <DialogHeader><DialogTitle>Differenz erfassen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Typ</Label>
              <Select value={diffForm.typ} onValueChange={(v) => setDiffForm({...diffForm, typ: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DIFF_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Datum</Label><Input type="date" value={diffForm.datum} onChange={(e) => setDiffForm({...diffForm, datum: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Tournummer</Label><Input value={diffForm.tournummer} onChange={(e) => setDiffForm({...diffForm, tournummer: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Fahrer</Label><Input value={diffForm.fahrer} onChange={(e) => setDiffForm({...diffForm, fahrer: e.target.value})} /></div>
            <div className="space-y-2"><Label>Betrag (CHF)</Label><Input type="number" step="0.01" value={diffForm.betrag} onChange={(e) => setDiffForm({...diffForm, betrag: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Kommentar</Label><Textarea value={diffForm.kommentar} onChange={(e) => setDiffForm({...diffForm, kommentar: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)}>Abbrechen</Button>
            <Button onClick={addDiff} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="diff-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
